import { LocationStrategy } from '@angular/common';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  Command,
  CommandResponse,
  MutateOperation,
  MutationCommand,
  conformsTo,
  getUUID,
  isCommandResponse,
  isString,
  objectToMutationCommand,
  shareLast,
} from '@dmb/core';

import { CommsTypeObject, DmbFontSize, DmbTheme, UserProfile, WebAuth, anon, decodeToAuthenticated } from '../model';
import { AuthData } from '../model/user/login';
import { CommsAuthService, CommsService, LoadingService, RxStompService } from '../services';
import { FingerprintService } from '../services/fingerprint/fingerprint.service';

const apiUrl = '/api/v4/public';

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);
  private commsAuth = inject(CommsAuthService);
  private comms = inject(CommsService);
  private fingerprintService = inject(FingerprintService);
  private loadingService = inject(LoadingService);
  private stompService = inject(RxStompService);
  private loc = inject(LocationStrategy);

  private imageReload = new BehaviorSubject(true);
  private profile = this.comms.onMessage('/user/profile').pipe(map(decodeToAuthenticated), shareLast());
  commandResponse = this.comms.onMessage('/user/command').pipe(
    filter((message): message is CommandResponse => {
      if (isCommandResponse(message)) {
        return true;
      }

      console.warn('Received non CommandResponse message', message);
      return false;
    }),
    shareReplay(0),
  );
  profileImage = this.imageReload.pipe(
    mergeMap(() =>
      this.http.get<{
        status: string;
        image: string;
      }>(`${apiUrl}/users/profile_image`),
    ),
    shareLast(),
  );

  user: Observable<UserProfile> = this.commsAuth.authenticated.pipe(
    mergeMap((isAuthenticated) => (isAuthenticated ? this.profile : of(anon))),
    shareLast(),
  );

  language = this.profile.pipe(
    map((u) => u.language),
    distinctUntilChanged(),
    shareLast(),
  );

  languageSignal = toSignal(this.language, { initialValue: 'en' });

  fontSize = this.profile.pipe(
    map((u) => u.preferences.fontSize),
    distinctUntilChanged(),
    shareLast(),
  );

  theme: Observable<DmbTheme> = this.profile.pipe(
    map((p) => p.preferences['theme'] || 'standard'),
    distinctUntilChanged(),
    shareLast(),
  );

  preferences = this.profile.pipe(map((p) => p.preferences));
  /**
   * This will only emit a user when they are authenticated.
   * WARNING! It will not emit when the user logs out or is set to null.
   * It is primarily useful as a trigger after the user is authenticated.
   *
   * @type {Observable<User>}
   */
  authenticatedUser = this.user.pipe(
    filter((u) => u != null && u.authenticated),
    distinctUntilChanged((a, b) => a.username === b.username),
    shareLast(),
  );

  v2AccessToken = this.commsAuth.authenticated.pipe(
    filter((a) => a),
    switchMap(() => this.http.post('/api/v4/public/users/auth_for_token', null)),
    map((auth) => {
      if (!('access_token' in auth) || typeof auth.access_token !== 'string') {
        throw new Error('Did not receive access_token');
      }

      return 'Bearer ' + auth.access_token;
    }),
    shareReplay(1),
  );

  private getBrowserFingerprint() {
    try {
      return localStorage.getItem('browserFingerprint');
    } catch {
      return null;
    }
  }

  public get fingerprint(): Observable<string> {
    try {
      const browserFingerprint = this.getBrowserFingerprint();

      return browserFingerprint === null
        ? from(this.fingerprintService.getFingerprint()).pipe(filter((value) => value !== null))
        : of(browserFingerprint);
    } catch {
      console.log('Enable 3rd party cookies to have the full authentication feature');
      return new Observable<string>();
    }
  }

  expectResponse(cmdId: string) {
    return this.commandResponse.pipe(
      filter((cmd) => cmd.command.id === cmdId),
      take(1),
    );
  }

  async reload() {
    await this.stompService.deactivate({ force: true });
    this.stompService.activate();
  }

  requestPasswordReset(userNameOrMail: string) {
    return this.http.post(`${apiUrl}/users/reset_password`, { email: userNameOrMail });
  }

  reloadProfileImage() {
    this.imageReload.next(true);
  }

  public login(payload: WebAuth, browserFingerprint?: string): Observable<AuthData> {
    const cmd: Command<WebAuth> = {
      id: getUUID(),
      key: 'DMBWebUI:Login',
      path: 'users/auth',
      payload: {
        ...payload,
        browserFingerprint,
      },
    };

    this.loadingService.setLoading(true);
    return this.http.post<AuthData>(`${apiUrl}/${cmd.path}`, cmd).pipe(
      tap(() => this.refreshToken()),
      mergeMap((r: AuthData) => this.authenticatedUser.pipe(map(() => r))),
      finalize(() => this.loadingService.setLoading(false)),
    );
  }

  refreshToken() {
    this.commsAuth.refreshToken();
  }

  logout() {
    this.http.get('/api/v4/public/users/logout').subscribe(() => location.assign(this.loc.getBaseHref()));
  }

  public resetPassword(id: number, secret: string, password: string) {
    return this.http
      .post<CommandResponse>('/api/v4/public/users/update_password', {
        id,
        secret,
        cmd: objectToMutationCommand(`DMBWebUI:ProfileEditor`, 'users/profile', { password }),
      })
      .pipe(
        catchError((e) => {
          if (e.status === 422) {
            const err = e.error;
            if (isCommandResponse(err)) {
              return of(err);
            }
          }
          throw e;
        }),
      );
  }

  uploadProfileImage(file: File): Observable<HttpEvent<unknown>> {
    const formData = new FormData();
    formData.append('image', file);
    return this.commsAuth.token.pipe(
      switchMap((token) =>
        this.http.post<HttpEvent<unknown>>(`${apiUrl}/users/image`, formData, {
          headers: { 'X-XSRF-TOKEN': token },
          reportProgress: true,
          observe: 'events',
          responseType: 'json',
        }),
      ),
    );
  }

  public addComms(payload: CommsTypeObject) {
    const cmd = new MutationCommand(`DMBWebUI:ProfileEditor`, 'users/profile', [
      new MutateOperation('comms', 'add', payload),
    ]);
    this.comms.send('/command', cmd);
    return this.expectResponse(cmd.id);
  }

  public sendUpdate(obj: Record<string | symbol | number, unknown>, src = 'ProfileEditor') {
    const cmd = objectToMutationCommand(`DMBWebUI:${src}`, 'users/profile', obj);
    this.comms.send('/command', cmd);
    return this.expectResponse(cmd.id);
  }

  /**
   * Loads the base64 encoded PNG of the TOTP QR code for the current user.
   * Emits null if the response does not contain a code.
   */
  totpQrCode(): Observable<string | null> {
    return this.http
      .get<unknown>(`${apiUrl}/users/otp`)
      .pipe(map((v) => (conformsTo({ code: isString })(v) ? v.code : null)));
  }

  enableTotp(code: string) {
    this.comms.send(
      '/command',
      new MutationCommand(`DMBWebUI:ProfileEditor`, 'users/profile', [
        new MutateOperation('totpCode', 'replace', code),
      ]),
    );
  }

  setTheme(theme: DmbTheme) {
    return this.sendUpdate({ 'preferences.theme': theme });
  }

  setFontSize(fontSize: DmbFontSize) {
    return this.sendUpdate({ 'preferences.fontSize': fontSize });
  }

  deleteComms(payload: number) {
    this.comms.send(
      '/command',
      new MutationCommand(`DMBWebUI:ProfileEditor`, 'users/profile', [new MutateOperation('comms', 'remove', payload)]),
    );
  }

  authenticateWithFingerPrint(formValue: WebAuth): Observable<AuthData> {
    if (!this.fingerprint) return new Observable();

    return this.fingerprint.pipe(
      switchMap((hash: string) =>
        this.login(formValue, hash).pipe(
          tap(() => {
            try {
              localStorage.setItem('browserFingerprint', hash);
            } catch {
              console.log('Enable 3rd party cookies to have the full authentication feature');
            }
          }),
          take(1),
        ),
      ),
    );
  }
}
